import { useState } from 'react';
import type { NgrpSummary, OuterPayload } from '../../../types/analyticstypes';
import { formatMoney, formatRate } from '../../../utils/analytics/format';

export default function NgrpTab({
  ngrp,
  outer,
}: {
  ngrp?: NgrpSummary;
  outer: OuterPayload;
}) {
  const [showLines, setShowLines] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [showTenderReq, setShowTenderReq] = useState(false);

  // The tender error and raw TENDERED request live on the basket, so they can be
  // present even when settlement (ngrp) never came back. Render them in both cases.
  const tenderErrorBox = outer.ngrpTenderError ? (
    <div className="border border-red-200 bg-red-50 rounded-lg p-4">
      <h4 className="text-sm font-semibold text-red-800 mb-1">Tender error</h4>
      <p className="text-xs text-red-700 font-mono whitespace-pre-wrap break-words">
        {outer.ngrpTenderError}
      </p>
    </div>
  ) : null;

  const tenderRequestBlock =
    outer.ngrpTenderRequestRaw != null ? (
      <div className="border border-gray-200 rounded-lg bg-white">
        <button
          onClick={() => setShowTenderReq((v) => !v)}
          className="w-full text-left p-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
        >
          <svg
            className={`w-3 h-3 transition-transform ${showTenderReq ? 'rotate-90' : ''}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
          </svg>
          Show raw TENDERED request
        </button>
        {showTenderReq && (
          <pre className="bg-gray-900 text-gray-100 text-[10px] p-3 rounded-b overflow-x-auto max-h-[500px] font-mono border-t border-gray-100">
            {typeof outer.ngrpTenderRequestRaw === 'string'
              ? outer.ngrpTenderRequestRaw
              : JSON.stringify(outer.ngrpTenderRequestRaw, null, 2)}
          </pre>
        )}
      </div>
    ) : null;

  if (!ngrp) {
    return (
      <div className="space-y-4">
        {tenderErrorBox}
        {tenderRequestBlock}
        <div className="text-sm text-gray-500 italic p-6 text-center bg-gray-50 rounded-lg">
          No NGRP settlement data
          {outer.status === 'ABANDONED' ? ' — session never reached the POS.' : ' for this transaction.'}
        </div>
      </div>
    );
  }

  const currency = ngrp.itemLines?.[0]?.posCode ? 'EUR' : 'USD';
  const totalDelta = ngrp.total !== outer.totalAmount && outer.totalAmount > 0;

  return (
    <div className="space-y-4">
      {tenderErrorBox}

      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">Totals</h4>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <span className="text-gray-500">Subtotal</span>
          <span className="font-mono text-right text-gray-900">{formatMoney(ngrp.subtotal, currency)}</span>
          <span className="text-gray-500">Tax</span>
          <span className="font-mono text-right text-gray-900">{formatMoney(ngrp.taxTotal, currency)}</span>
          {ngrp.discountTotal > 0 && (
            <>
              <span className="text-gray-500">Discount</span>
              <span className="font-mono text-right text-red-700">−{formatMoney(ngrp.discountTotal, currency)}</span>
            </>
          )}
          {ngrp.loyaltyDiscountTotal > 0 && (
            <>
              <span className="text-gray-500">Loyalty discount</span>
              <span className="font-mono text-right text-red-700">−{formatMoney(ngrp.loyaltyDiscountTotal, currency)}</span>
            </>
          )}
          <span className="font-semibold text-gray-900 border-t pt-1">Total</span>
          <span className="font-mono text-right font-semibold text-gray-900 border-t pt-1">
            {formatMoney(ngrp.total, currency)}
          </span>
        </div>
        {totalDelta && (
          <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
            ⚠ NGRP total ({formatMoney(ngrp.total, currency)}) differs from basket total (
            {formatMoney(outer.totalAmount, currency)}).
          </p>
        )}
      </div>

      {((ngrp.orderTaxes && ngrp.orderTaxes.length > 0) ||
        (ngrp.itemTaxes && ngrp.itemTaxes.length > 0)) && (
        <div className="border border-gray-200 rounded-lg p-4 bg-white">
          <h4 className="text-sm font-semibold text-gray-800 mb-3">Tax Breakdown</h4>
          {ngrp.orderTaxes && ngrp.orderTaxes.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="py-1 font-medium">Description</th>
                  <th className="py-1 font-medium text-right">Rate</th>
                  <th className="py-1 font-medium text-right">Taxable</th>
                  <th className="py-1 font-medium text-right">Collected</th>
                </tr>
              </thead>
              <tbody>
                {ngrp.orderTaxes.map((t, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    <td className="py-1 text-gray-700">{t.description}</td>
                    <td className="py-1 text-right font-mono">{formatRate(t.taxRate * 100)}</td>
                    <td className="py-1 text-right font-mono">{formatMoney(t.taxableSalesAmount, currency)}</td>
                    <td className="py-1 text-right font-mono">{formatMoney(t.collectedAmount, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="border border-gray-200 rounded-lg p-4 bg-white">
        <h4 className="text-sm font-semibold text-gray-800 mb-3">POS Metadata</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
          {ngrp.octaneOrderId && (
            <div className="flex justify-between gap-2"><span className="text-gray-500">Octane Order ID</span><span className="font-mono text-gray-900 truncate" title={ngrp.octaneOrderId}>{ngrp.octaneOrderId}</span></div>
          )}
          {ngrp.storeId && (
            <div className="flex justify-between gap-2"><span className="text-gray-500">Store</span><span className="font-mono text-gray-900">{ngrp.storeId}</span></div>
          )}
          {ngrp.customerOrderCode && (
            <div className="flex justify-between gap-2"><span className="text-gray-500">Customer Code</span><span className="font-mono text-gray-900">{ngrp.customerOrderCode}</span></div>
          )}
          {ngrp.status && (
            <div className="flex justify-between gap-2"><span className="text-gray-500">Status</span><span className="font-semibold text-gray-900">{ngrp.status}</span></div>
          )}
          {ngrp.paymentInfo?.posEod !== undefined && (
            <div className="flex justify-between gap-2">
              <span className="text-gray-500" title="Sent to Aurus as POSBatchNumber for reconciliation">POS EOD Batch</span>
              <span className="font-mono font-semibold text-gray-900">{String(ngrp.paymentInfo.posEod)}</span>
            </div>
          )}
          {ngrp.paymentInfo?.eod !== undefined && (
            <div className="flex justify-between gap-2"><span className="text-gray-500" title="Legacy gateway batch number">EOD Batch (gateway)</span><span className="font-mono text-gray-900">{String(ngrp.paymentInfo.eod)}</span></div>
          )}
          {ngrp.paymentInfo?.transactionNo !== undefined && (
            <div className="flex justify-between gap-2"><span className="text-gray-500">Transaction #</span><span className="font-mono text-gray-900">{String(ngrp.paymentInfo.transactionNo)}</span></div>
          )}
          {ngrp.paymentInfo?.paymentMethod && (
            <div className="flex justify-between gap-2"><span className="text-gray-500">Payment Method</span><span className="font-mono text-gray-900">{ngrp.paymentInfo.paymentMethod}</span></div>
          )}
          {ngrp.paymentInfo?.orderCreatedAt && (
            <div className="flex justify-between gap-2"><span className="text-gray-500">POS Created</span><span className="font-mono text-gray-900 truncate">{ngrp.paymentInfo.orderCreatedAt}</span></div>
          )}
          {ngrp.customerIdentification && ngrp.customerIdentification.length > 0 && (
            <div className="flex justify-between gap-2">
              <span className="text-gray-500">PAR Token</span>
              <span
                className="font-mono text-gray-700 truncate"
                title={ngrp.customerIdentification.map((c) => `${c.type}:${c.value}`).join(', ')}
              >
                {ngrp.customerIdentification.map((c) => `${c.type}:${c.value}`).join(', ')}
              </span>
            </div>
          )}
        </div>
        {ngrp.requireOperatorValidationOnCheckout && (
          <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] bg-amber-50 text-amber-800 border border-amber-200">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Operator validation required (age-restricted items)
          </div>
        )}
      </div>

      {ngrp.itemLines && ngrp.itemLines.length > 0 && (
        <div className="border border-gray-200 rounded-lg bg-white">
          <button
            onClick={() => setShowLines((v) => !v)}
            className="w-full text-left p-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <svg
              className={`w-3 h-3 transition-transform ${showLines ? 'rotate-90' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            POS item lines ({ngrp.itemLines.length})
          </button>
          {showLines && (
            <div className="overflow-x-auto border-t border-gray-100">
              <table className="w-full text-xs min-w-[600px]">
                <thead>
                  <tr className="text-left text-gray-500 bg-gray-50">
                    <th className="py-1.5 px-3 font-medium">#</th>
                    <th className="py-1.5 px-3 font-medium">Description</th>
                    <th className="py-1.5 px-3 font-medium text-right">Qty</th>
                    <th className="py-1.5 px-3 font-medium text-right">Amount</th>
                    <th className="py-1.5 px-3 font-medium">POS Code</th>
                  </tr>
                </thead>
                <tbody>
                  {ngrp.itemLines.map((l, i) => {
                    const isChild = !!l.parentItemLineId;
                    return (
                      <tr key={l.itemLineId || i} className="border-b border-gray-50">
                        <td className="py-1.5 px-3 font-mono text-gray-500">{l.lineSeq ?? i + 1}</td>
                        <td className={`py-1.5 px-3 ${isChild ? 'pl-6 text-gray-600' : 'text-gray-900'}`}>
                          {isChild && <span className="text-gray-400 mr-1">↳</span>}
                          {l.description}
                          {l.voided && <span className="ml-2 text-[10px] text-red-600 line-through">VOIDED</span>}
                        </td>
                        <td className="py-1.5 px-3 text-right font-mono">{l.quantity}</td>
                        <td className="py-1.5 px-3 text-right font-mono">{formatMoney(l.extendedAmount, currency)}</td>
                        <td className="py-1.5 px-3 font-mono text-gray-500 text-[10px]">{l.posCode || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {ngrp.raw != null && (
        <div className="border border-gray-200 rounded-lg bg-white">
          <button
            onClick={() => setShowRaw((v) => !v)}
            className="w-full text-left p-3 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
          >
            <svg
              className={`w-3 h-3 transition-transform ${showRaw ? 'rotate-90' : ''}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
            Show raw NGRP response
          </button>
          {showRaw && (
            <pre className="bg-gray-900 text-gray-100 text-[10px] p-3 rounded-b overflow-x-auto max-h-[500px] font-mono border-t border-gray-100">
              {JSON.stringify(ngrp.raw, null, 2)}
            </pre>
          )}
        </div>
      )}

      {tenderRequestBlock}
    </div>
  );
}
